import { readUploadFacadeErrorCode, readUploadRefusalTrigger } from "./uploadFacadeErrors";
import type { MultipartApiPort, MultipartSessionShape, UploadLease, UploadTelemetry } from "./ports";
import { uploadServerRefusalLogDetails } from "./uploadTelemetry";

/**
 * LSP-23082: the client-owned durable marker for "the server answered for this upload and proved the attempt this
 * device holds cannot be resumed". Deliberately not an auth code and not an evidence terminal: the row parks
 * needs-attention with its bytes, and the requeue pass may try it again.
 */
export const UPLOAD_ATTEMPT_UNRESUMABLE_CODE = "UPLOAD_ATTEMPT_UNRESUMABLE";

export type RefusedAttemptProbeVerdict<Session> =
	| { kind: "not-applicable" }
	| { kind: "unproven"; reason: string }
	| { kind: "rebound"; session: Session }
	| { kind: "unresumable"; reason: string; error: Error };

type ReceiptAttempt = { id: string; generation: number; takeGeneration?: unknown; transport?: unknown };

function readReceiptAttempt(receipt: unknown): ReceiptAttempt | null | undefined {
	if (!receipt || typeof receipt !== "object" || !("attempt" in receipt)) return undefined;
	const attempt = (receipt as { attempt?: unknown }).attempt;
	if (attempt === null) return null;
	if (!attempt || typeof attempt !== "object") return undefined;
	const { id, generation } = attempt as { id?: unknown; generation?: unknown };
	if (typeof id !== "string" || id.length === 0 || !Number.isSafeInteger(generation) || (generation as number) < 1) return undefined;
	return attempt as ReceiptAttempt;
}

/**
 * LSP-23082: a `/uploads/:id/urls` refresh answered 404 `UPLOAD_NOT_FOUND`. That code conflates "no such upload /
 * not yours" (R-2: a pause, never a verdict) with "the attempt you hold is not the current, active one" (which no
 * credential event can fix, so treating it as auth-gated retried a customer's upload forever). Ask the server once,
 * through the unfenced receipt, which one it is:
 *
 *  - the receipt read is refused or fails -> `unproven`: the caller keeps its existing classification (auth-gated).
 *  - the receipt names a different current attempt of the same take -> list parts with the held fence, which is the
 *    server's coordinate-verified migration announcement; the port adopts it. Only an adopted attempt is `rebound`.
 *    The receipt alone never rebinds: it does not prove the new attempt writes to the same S3 multipart upload.
 *  - anything else -> `unresumable`, and the caller parks the row needs-attention with its bytes.
 */
export async function probeRefusedUploadAttempt<Session extends MultipartSessionShape>(input: {
	session: Session;
	error: unknown;
	lease: UploadLease<Session>;
	multipartApi: MultipartApiPort<Session>;
	getById(sessionId: string): Promise<Session | undefined>;
	telemetry: UploadTelemetry;
}): Promise<RefusedAttemptProbeVerdict<Session>> {
	const { session, error, multipartApi } = input;
	if (
		!multipartApi.readReceipt ||
		session.endpointFamily !== "uploads" ||
		typeof session.registryId !== "string" ||
		readUploadFacadeErrorCode(error) !== "UPLOAD_NOT_FOUND" ||
		readUploadRefusalTrigger(error) !== "urls"
	) {
		return { kind: "not-applicable" };
	}
	const context: Record<string, unknown> = {
		sessionId: session.id,
		registryId: session.registryId,
		heldAttemptId: session.attemptId ?? null,
		heldGeneration: session.generation ?? null
	};
	const settle = <Verdict extends RefusedAttemptProbeVerdict<Session>>(
		verdict: Verdict,
		details: Record<string, unknown> = {}
	): Verdict => {
		try {
			input.telemetry.logWarn("recording.fileUpload.refusedAttemptProbe", {
				...context,
				...details,
				outcome: verdict.kind,
				reason: "reason" in verdict ? verdict.reason : null
			});
		} catch {
			// The verdict must not depend on the observability adapter.
		}
		return verdict;
	};
	const unresumable = (reason: string) => {
		const unresumableError = Object.assign(
			new Error(`The server cannot resume the upload attempt this device holds (${reason}) [${UPLOAD_ATTEMPT_UNRESUMABLE_CODE}]`),
			{ code: UPLOAD_ATTEMPT_UNRESUMABLE_CODE }
		);
		return settle({ kind: "unresumable", reason, error: unresumableError });
	};

	let receipt: unknown;
	try {
		receipt = await multipartApi.readReceipt({ registryId: session.registryId });
	} catch (receiptError) {
		const refusal = uploadServerRefusalLogDetails(receiptError);
		const refused = refusal.httpStatus === 401 || refusal.httpStatus === 403 || refusal.httpStatus === 404;
		return settle({ kind: "unproven", reason: refused ? "receipt-read-refused" : "receipt-read-failed" }, refusal);
	}
	const current = readReceiptAttempt(receipt);
	if (current === undefined) return settle({ kind: "unproven", reason: "receipt-malformed" });
	const receiptStatus = (receipt as { status?: unknown }).status;
	Object.assign(context, {
		receiptStatus: typeof receiptStatus === "string" ? receiptStatus : null,
		currentAttemptId: current?.id ?? null,
		currentGeneration: current?.generation ?? null
	});
	if (current === null) return unresumable("no-current-attempt");
	if (current.id === session.attemptId && current.generation === session.generation) return unresumable("current-attempt-refused");
	if (current.transport !== undefined && current.transport !== "multipart") return unresumable("transport-changed");
	if (session.takeGeneration !== undefined && current.takeGeneration !== undefined && current.takeGeneration !== session.takeGeneration) {
		return unresumable("take-changed");
	}
	try {
		await multipartApi.listParts(session, { lease: input.lease, ownsCurrentSession: () => input.lease.owns(session) });
	} catch (partsError) {
		if (readUploadFacadeErrorCode(partsError) === "UPLOAD_NOT_FOUND") return unresumable("migration-refused");
		return settle({ kind: "unproven", reason: "parts-read-failed" }, uploadServerRefusalLogDetails(partsError));
	}
	let reread: Session | undefined;
	try {
		reread = await input.getById(session.id);
	} catch {
		// A local storage failure proves nothing about the server; the caller keeps its classification.
		return settle({ kind: "unproven", reason: "reread-failed" });
	}
	if (reread && reread.attemptId === current.id && reread.generation === current.generation) {
		return settle({ kind: "rebound", session: reread });
	}
	return unresumable("migration-not-announced");
}
